const JPEG_START_OF_IMAGE = 0xd8;
const JPEG_START_OF_SCAN = 0xda;
const JPEG_END_OF_IMAGE = 0xd9;
const JPEG_APP1 = 0xe1;

const EXIF_IDENTIFIER = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00];

export class JpegMetadataSanitizationError extends Error {
  constructor(message) {
    super(message);
    this.name = "JpegMetadataSanitizationError";
  }
}

const hasBytes = (bytes, offset, length) =>
  offset >= 0 && offset + length <= bytes.length;

const bytesMatch = (bytes, offset, expected) =>
  hasBytes(bytes, offset, expected.length) &&
  expected.every((value, index) => bytes[offset + index] === value);

const readUint16 = (bytes, offset, littleEndian) => {
  if (!hasBytes(bytes, offset, 2)) return null;

  return littleEndian
    ? bytes[offset] | (bytes[offset + 1] << 8)
    : (bytes[offset] << 8) | bytes[offset + 1];
};

const readUint32 = (bytes, offset, littleEndian) => {
  if (!hasBytes(bytes, offset, 4)) return null;

  if (littleEndian) {
    return (
      (bytes[offset] |
        (bytes[offset + 1] << 8) |
        (bytes[offset + 2] << 16) |
        (bytes[offset + 3] << 24)) >>>
      0
    );
  }

  return (
    (((bytes[offset] << 24) >>> 0) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]) >>>
    0
  );
};

export const extractExifOrientation = (app1Payload) => {
  if (!bytesMatch(app1Payload, 0, EXIF_IDENTIFIER)) return null;

  const tiffOffset = EXIF_IDENTIFIER.length;
  if (!hasBytes(app1Payload, tiffOffset, 8)) return null;

  const littleEndian =
    app1Payload[tiffOffset] === 0x49 && app1Payload[tiffOffset + 1] === 0x49;
  const bigEndian =
    app1Payload[tiffOffset] === 0x4d && app1Payload[tiffOffset + 1] === 0x4d;

  if (!littleEndian && !bigEndian) return null;
  if (readUint16(app1Payload, tiffOffset + 2, littleEndian) !== 42) return null;

  const firstIfdOffset = readUint32(app1Payload, tiffOffset + 4, littleEndian);
  if (firstIfdOffset == null) return null;

  const ifdOffset = tiffOffset + firstIfdOffset;
  const entryCount = readUint16(app1Payload, ifdOffset, littleEndian);
  if (entryCount == null) return null;

  for (let index = 0; index < entryCount; index += 1) {
    const entryOffset = ifdOffset + 2 + index * 12;
    if (!hasBytes(app1Payload, entryOffset, 12)) return null;

    const tag = readUint16(app1Payload, entryOffset, littleEndian);
    if (tag !== 0x0112) continue;

    const type = readUint16(app1Payload, entryOffset + 2, littleEndian);
    const count = readUint32(app1Payload, entryOffset + 4, littleEndian);
    if (type !== 3 || count !== 1) return null;

    const orientation = readUint16(app1Payload, entryOffset + 8, littleEndian);

    return orientation >= 1 && orientation <= 8 ? orientation : null;
  }

  return null;
};

const minimalOrientationExifSegment = (orientation) => {
  // APP1 length includes its own two length bytes, but not the FF/E1 marker.
  const payloadLength = EXIF_IDENTIFIER.length + 26;
  const segment = new Uint8Array(payloadLength + 4);
  const segmentLength = payloadLength + 2;

  segment.set([0xff, JPEG_APP1, segmentLength >> 8, segmentLength & 0xff], 0);
  segment.set(EXIF_IDENTIFIER, 4);

  const tiffOffset = 4 + EXIF_IDENTIFIER.length;
  segment.set([0x49, 0x49, 0x2a, 0x00, 0x08, 0x00, 0x00, 0x00], tiffOffset);

  const ifdOffset = tiffOffset + 8;
  segment.set(
    [
      0x01,
      0x00, // one IFD entry
      0x12,
      0x01, // Orientation tag
      0x03,
      0x00, // SHORT
      0x01,
      0x00,
      0x00,
      0x00, // count = 1
      orientation,
      0x00,
      0x00,
      0x00, // inline SHORT value
      0x00,
      0x00,
      0x00,
      0x00, // no next IFD
    ],
    ifdOffset,
  );

  return segment;
};

const isStandaloneMarker = (marker) =>
  marker === 0x01 ||
  marker === JPEG_START_OF_IMAGE ||
  marker === JPEG_END_OF_IMAGE ||
  (marker >= 0xd0 && marker <= 0xd7);

const concatParts = (parts) => {
  const byteLength = parts.reduce((total, part) => total + part.length, 0);
  const output = new Uint8Array(byteLength);
  let offset = 0;

  parts.forEach((part) => {
    output.set(part, offset);
    offset += part.length;
  });

  return output;
};

const sanitizedJpegParts = (input) => {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);

  if (
    bytes.length < 4 ||
    bytes[0] !== 0xff ||
    bytes[1] !== JPEG_START_OF_IMAGE
  ) {
    throw new JpegMetadataSanitizationError(
      "Cloudinary reported DNG, but the file is not a JPEG container.",
    );
  }

  const parts = [bytes.subarray(0, 2)];
  let offset = 2;
  let app1InsertIndex = null;
  let orientation = null;
  let removedApp1 = false;
  let foundScan = false;

  while (offset < bytes.length) {
    const markerStart = offset;
    if (bytes[offset] !== 0xff) {
      throw new JpegMetadataSanitizationError(
        "The JPEG metadata structure is invalid.",
      );
    }

    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1;
    if (offset >= bytes.length) break;

    const marker = bytes[offset];
    offset += 1;

    if (marker === JPEG_START_OF_SCAN) {
      parts.push(bytes.subarray(markerStart));
      foundScan = true;
      break;
    }

    if (isStandaloneMarker(marker)) {
      parts.push(bytes.subarray(markerStart, offset));
      if (marker === JPEG_END_OF_IMAGE) break;
      continue;
    }

    const segmentLength = readUint16(bytes, offset, false);
    if (segmentLength == null || segmentLength < 2) {
      throw new JpegMetadataSanitizationError(
        "The JPEG contains an invalid metadata segment.",
      );
    }

    const segmentEnd = offset + segmentLength;
    if (segmentEnd > bytes.length) {
      throw new JpegMetadataSanitizationError(
        "The JPEG contains a truncated metadata segment.",
      );
    }

    if (marker === JPEG_APP1) {
      const payload = bytes.subarray(offset + 2, segmentEnd);
      orientation ??= extractExifOrientation(payload);
      app1InsertIndex ??= parts.length;
      removedApp1 = true;
    } else {
      parts.push(bytes.subarray(markerStart, segmentEnd));
    }

    offset = segmentEnd;
  }

  if (!foundScan) {
    throw new JpegMetadataSanitizationError(
      "The JPEG does not contain readable image data.",
    );
  }

  if (!removedApp1) {
    throw new JpegMetadataSanitizationError(
      "The JPEG does not contain metadata that can be sanitised.",
    );
  }

  if (orientation != null) {
    parts.splice(
      app1InsertIndex,
      0,
      minimalOrientationExifSegment(orientation),
    );
  }

  return parts;
};

export const sanitizeJpegBytes = (input) =>
  concatParts(sanitizedJpegParts(input));

const readAsArrayBuffer = (blob) => {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result));
    reader.addEventListener("error", () => reject(reader.error));
    reader.readAsArrayBuffer(blob);
  });
};

export const sanitizeCloudinaryDngJpeg = async (file) => {
  const bytes = new Uint8Array(await readAsArrayBuffer(file));
  const parts = sanitizedJpegParts(bytes);

  return new File(parts, file.name, {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
};

export const cloudinaryErrorMessage = ({
  responseText = "",
  headerMessage = "",
} = {}) => {
  let responseMessage = "";

  try {
    const response = JSON.parse(responseText);
    responseMessage = response?.error?.message || response?.message || "";
  } catch (_error) {
    // Cloudinary normally returns JSON, but the response header is also useful.
  }

  return responseMessage || headerMessage || responseText;
};

export const createCloudinaryUploadError = ({
  status,
  responseText,
  headerMessage,
  fallbackMessage = "Cloudinary upload failed",
}) => {
  const cloudinaryMessage = cloudinaryErrorMessage({
    responseText,
    headerMessage,
  });
  const error = new Error(
    cloudinaryMessage || `${fallbackMessage} with status ${status}`,
  );

  error.cloudinaryMessage = cloudinaryMessage;
  error.status = status;
  return error;
};

export const isCloudinaryDngFormatError = (error) => {
  const message = [error?.cloudinaryMessage, error?.message]
    .filter(Boolean)
    .join(" ");

  return /image file format dng not allowed/i.test(message);
};

export const uploadWithCloudinaryDngRecovery = async ({
  file,
  upload,
  sanitize = sanitizeCloudinaryDngJpeg,
  onRecoveryStart,
}) => {
  try {
    return await upload(file);
  } catch (uploadError) {
    if (!isCloudinaryDngFormatError(uploadError)) throw uploadError;

    let sanitizedFile;
    try {
      sanitizedFile = await sanitize(file);
    } catch (sanitizationError) {
      uploadError.sanitizationError = sanitizationError;
      throw uploadError;
    }

    onRecoveryStart?.(sanitizedFile);

    // Deliberately call upload only once more. A second failure is returned to
    // the uploader and can never recurse into another recovery attempt.
    return upload(sanitizedFile);
  }
};
